import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { useCurrentUnit } from './useCurrentUnit.js'

// Drives the weekly Schedule screen.
//
// Pulls a 7-day window of schedule_assignments + active staff so the
// manager can see and edit a whole week at a glance. Returns the
// assignments grouped by day with per-day labor totals, plus a week
// total. Mutations cover the full edit lifecycle: create, update
// (any field), status-only quick changes, and delete.
//
// The `weekStartDate` arg is a YYYY-MM-DD string for the Monday of
// the week. The hook re-fetches when it changes (the screen flips
// it on prev/next-week navigation). Visibility-refresh + midnight
// rollover patterns match the other data hooks.
export function useScheduleData(weekStartDate) {
  const [assignments, setAssignments] = useState([])
  const [activeStaff, setActiveStaff] = useState([])
  // The current unit's pending approve-and-execute schedule fix (frozen by the
  // morning brief). null when there's nothing awaiting approval.
  const [pendingScheduleAction, setPendingScheduleAction] = useState(null)
  const [loading, setLoading]         = useState(true)
  const [error, setError]             = useState(null)

  // Multi-unit scope — null for single-unit (queries byte-identical).
  const { currentRestaurantId } = useCurrentUnit()

  const fetchAll = useCallback(async () => {
    if (!weekStartDate) return
    setLoading(true)
    setError(null)

    // Compute week end (Sunday) inside the callback so the dep array
    // stays minimal — avoids the "new value every render" cycle.
    const weekEnd = addDays(weekStartDate, 6)

    // Unit filter applied only when grouped; single-unit (null) untouched.
    const scope = (q) => (currentRestaurantId ? q.eq('restaurant_id', currentRestaurantId) : q)

    try {
      const [assignRes, staffRes, pendingRes] = await Promise.all([
        scope(supabase.from('schedule_assignments')
          .select(`
            id, shift_date, start_time, end_time, role, status, notes,
            staff:staff_id(id, name, role, hourly_wage)
          `)
          .gte('shift_date', weekStartDate)
          .lte('shift_date', weekEnd)
          .order('shift_date')
          .order('start_time')),
        scope(supabase.from('staff')
          .select('id, name, role, hourly_wage')
          .eq('active', true)
          .order('name')),
        // Pending approve-and-execute schedule fix for this unit (latest).
        // RLS already scopes to the caller's units; the unit filter narrows to
        // the SELECTED unit for grouped operators.
        scope(supabase
          .from('pending_schedule_actions')
          .select('id, restaurant_id, brief_date, spec, dollar_impact, status, created_at')
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(1)),
      ])
      if (assignRes.error) throw assignRes.error
      // staff / pending failures are non-fatal — staff: Settings can recover;
      // pending: the approve card just won't render.

      setAssignments(assignRes.data ?? [])
      setActiveStaff(staffRes.data ?? [])
      setPendingScheduleAction(pendingRes.data?.[0] ?? null)
    } catch (e) {
      setError(e)
      // eslint-disable-next-line no-console
      console.error('[useScheduleData]', e)
    } finally {
      setLoading(false)
    }
  }, [weekStartDate, currentRestaurantId])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Visibility refresh — when the manager comes back to the tab,
  // pull fresh data so external changes (e.g. another device, a
  // teammate updating status) are reflected.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') fetchAll()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [fetchAll])

  // ---------- Per-day rollups + week totals ------------------
  // Build a 7-element array, Mon..Sun, even for days that have no
  // shifts so the day picker always renders 7 cells. Match the
  // status logic from useHuddleData: only 'scheduled' and 'completed'
  // count toward labor cost. 'called_out' / 'no_show' / 'cancelled'
  // shifts still appear in the list so the manager has a record,
  // but they don't add to dollars or hours.
  const byDay = useMemo(() => {
    if (!weekStartDate) return []
    const days = []
    for (let i = 0; i < 7; i++) {
      const date = addDays(weekStartDate, i)
      days.push({
        date,
        assignments: [],
        cost: 0,
        hours: 0,
        staffCount: 0,
      })
    }
    const indexByDate = new Map(days.map((d, i) => [d.date, i]))

    for (const a of assignments) {
      const idx = indexByDate.get(a.shift_date)
      if (idx == null) continue
      const day = days[idx]
      day.assignments.push(a)
      if (a.status === 'scheduled' || a.status === 'completed') {
        const h = hoursBetween(a.start_time, a.end_time)
        const wage = Number(a.staff?.hourly_wage || 0)
        day.cost  += h * wage
        day.hours += h
      }
    }
    // Distinct staff count per day (counted shifts only)
    for (const d of days) {
      const ids = new Set()
      for (const a of d.assignments) {
        if (a.status === 'scheduled' || a.status === 'completed') {
          if (a.staff?.id) ids.add(a.staff.id)
        }
      }
      d.staffCount = ids.size
    }
    return days
  }, [assignments, weekStartDate])

  const weekTotals = useMemo(() => {
    let cost = 0, hours = 0, shiftCount = 0
    const staffSet = new Set()
    for (const d of byDay) {
      cost += d.cost
      hours += d.hours
      for (const a of d.assignments) {
        if (a.status === 'scheduled' || a.status === 'completed') {
          shiftCount++
          if (a.staff?.id) staffSet.add(a.staff.id)
        }
      }
    }
    return { cost, hours, shiftCount, staffCount: staffSet.size }
  }, [byDay])

  // ---------- Mutations -------------------------------------
  const createAssignment = useCallback(async (payload) => {
    // Single-unit (null): omit restaurant_id, the 0019 default trigger
    // fills it — byte-identical. Grouped: set it explicitly so the
    // shift lands on the SELECTED unit, not the trigger's first-membership.
    const { data, error: insErr } = await supabase
      .from('schedule_assignments')
      .insert({
        staff_id:   payload.staff_id,
        shift_date: payload.shift_date,
        start_time: payload.start_time,
        end_time:   payload.end_time,
        role:       payload.role || null,
        status:     payload.status || 'scheduled',
        notes:      payload.notes || null,
        ...(currentRestaurantId ? { restaurant_id: currentRestaurantId } : {}),
      })
      .select()
      .single()
    if (insErr) throw insErr
    await fetchAll()
    return data
  }, [fetchAll, currentRestaurantId])

  const updateAssignment = useCallback(async (id, patch) => {
    const { error: updErr } = await supabase
      .from('schedule_assignments')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (updErr) throw updErr
    await fetchAll()
  }, [fetchAll])

  // Status-only quick change. Used by the inline action menu.
  const updateAssignmentStatus = useCallback(async (id, status) => {
    return updateAssignment(id, { status })
  }, [updateAssignment])

  const deleteAssignment = useCallback(async (id) => {
    const { error: delErr } = await supabase
      .from('schedule_assignments')
      .delete()
      .eq('id', id)
    if (delErr) throw delErr
    await fetchAll()
  }, [fetchAll])

  // Approve-and-execute: cancel the over-40h shift for the pending action via
  // the audited execute-schedule-fix edge fn (notify -> APPROVE -> execute).
  // The fn is idempotent (double-tap / retry cancels EXACTLY ONE shift). On a
  // non-2xx response or an edge error we throw so the screen can surface a
  // friendly retry; on success we refresh so the approve card clears and the
  // cancelled shift shows its new status.
  const approveScheduleFix = useCallback(async (pendingActionId) => {
    const { data, error: fnErr } = await supabase.functions.invoke('execute-schedule-fix', {
      body: {
        pending_action_id: pendingActionId,
        ...(currentRestaurantId ? { restaurant_id: currentRestaurantId } : {}),
      },
    })
    if (fnErr) throw fnErr
    if (data && data.ok === false) throw new Error(data.message || 'execute_failed')
    await fetchAll()
    return data
  }, [fetchAll, currentRestaurantId])

  // Skip routes through the SAME audited fn (mode=skip) so the dismissal is
  // recorded. Refresh clears the card.
  const skipScheduleFix = useCallback(async (pendingActionId) => {
    const { data, error: fnErr } = await supabase.functions.invoke('execute-schedule-fix', {
      body: {
        pending_action_id: pendingActionId,
        mode: 'skip',
        ...(currentRestaurantId ? { restaurant_id: currentRestaurantId } : {}),
      },
    })
    if (fnErr) throw fnErr
    await fetchAll()
    return data
  }, [fetchAll, currentRestaurantId])

  return {
    assignments,
    activeStaff,
    byDay,
    weekTotals,
    pendingScheduleAction,
    loading,
    error,
    refresh: fetchAll,
    createAssignment,
    updateAssignment,
    updateAssignmentStatus,
    deleteAssignment,
    approveScheduleFix,
    skipScheduleFix,
  }
}

// ---------- Helpers ----------------------------------------
// Hours between two HH:MM strings, wrapping past midnight if end < start.
// Mirror of the helper in useHuddleData so a closing shift like
// 17:00 → 02:00 gives 9 hours, not -15.
function hoursBetween(start, end) {
  if (!start || !end) return 0
  const [sh, sm] = start.split(':').map(Number)
  const [eh, em] = end.split(':').map(Number)
  const startMin = sh * 60 + sm
  let endMin     = eh * 60 + em
  if (endMin < startMin) endMin += 24 * 60
  return Math.max(0, (endMin - startMin) / 60)
}

// Date-string arithmetic that's locale-immune.
// Returns YYYY-MM-DD for `dateStr + n days`.
function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number)
  const dt = new Date(y, m - 1, d, 0, 0, 0, 0)
  dt.setDate(dt.getDate() + n)
  const yy = dt.getFullYear()
  const mm = String(dt.getMonth() + 1).padStart(2, '0')
  const dd = String(dt.getDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}
